import type { Pet } from '.';

import PetSpeciesData from '../../airtable/PetSpecies';
import { getLocalDate, getLocalTimezone } from '../../utils/time';
import { duration } from '../../utils/duration';
import { tuple } from '../../utils/types';
import { FateState } from '../fate/fate.ruleset';
import ab from 'src/replicant/ruleset/ab';

export type PetState = typeof petStates[number];

export type PetAttribute = typeof petAttributes[number];

export type PetSpecies = typeof species[number];

export type PetEvolution = typeof PetSpeciesData[number];

export type PetEvolutionStage = PetEvolution['stage'];

type AttributeDecayValues = Record<PetSpecies, Record<PetAttribute, number>>;

export type PetStatusEffect = typeof petStatusEffects[number];

export const petStates = [
  'dead',
  'sleeping',
  'dying',
  'had_visitor',
  'evolving',
  'want_to_go_out',
  'sick',
  'tooth_decay',
  'scared',
  'very_dirty',
  'very_hungry',
  'very_lonely',
  'very_pee',
  'very_sad',
  'dirty',
  'hungry',
  'lonely',
  'pee',
  'sad',
  'happy',
  'normal',
] as const;

export function isTerminalState(stage: PetEvolutionStage, state: PetState) {
  // Without death feature (control)
  switch (state) {
    case 'evolving':
      return true;
    case 'sick':
      return timeToEvolve[stage] === undefined;
    default:
      return false;
  }
}

export function getPetState(
  attributes: Record<PetAttribute, number>,
  statusEffects: Partial<Record<PetStatusEffect, boolean>>,
  isVisiting: boolean,
): PetState {
  switch (true) {
    case statusEffects.dead:
      return 'dead';
    case statusEffects.asleep:
      return 'sleeping';
    case statusEffects.dying:
      return 'dying';
    case statusEffects.sick:
      return 'sick';
    case statusEffects.evolving && !isVisiting:
      return 'evolving';
    case statusEffects.wantToGoOut && !isVisiting:
      return 'want_to_go_out';
    case statusEffects.toothDecay:
      return 'tooth_decay';
    case statusEffects.scared:
      return 'scared';
    case attributes.dirtiness > 70:
      return 'very_dirty';
    case attributes.stomach < 15:
      return 'very_hungry';
    case attributes.love < 15:
      return 'very_lonely';
    case attributes.urine > 85:
      return 'very_pee';
    case attributes.dirtiness > 40:
      return 'dirty';
    case attributes.stomach < 40:
      return 'hungry';
    case attributes.love < 40:
      return 'lonely';
    case attributes.urine > 60:
      return 'pee';
    case getStressValue(attributes) < 15:
      return 'happy';
    default:
      return 'normal';
  }
}

export function getPetStateFromFate(fate: FateState): PetState {
  if (fate === 'asleep') {
    return 'sleeping';
  }
  if (fate === 'hadVisitor') {
    return 'had_visitor';
  }
  if (fate === 'wantToGoOut') {
    return 'want_to_go_out';
  }
  if (fate === 'toothDecay') {
    return 'tooth_decay';
  }
  return fate;
}

export const petAttributes = [
  'stomach',
  'love',
  'dirtiness',
  'urine',
  'toothAcidity',
] as const;

export const attributeBounds = {
  min: 0,
  max: 100,
};

export const getStressValue = ({
  stomach,
  love,
  dirtiness,
  urine,
}: Record<PetAttribute, number>) =>
  ((attributeBounds.max - stomach) * 4 +
    (attributeBounds.max - love) * 3 +
    dirtiness * 5 +
    urine * 3) /
  15;

export const nextSpeciesWeights = PetSpeciesData.reduce(
  (acc, data) => ({ ...acc, [data.id]: data.next_weights }),
  {},
) as Record<PetSpecies, Record<PetSpecies, number>>;

export const decayIntervals = PetSpeciesData.reduce((acc, data) => {
  acc[data.id] = {
    dirtiness: duration({ seconds: data.dirtiness_sec }),
    love: duration({ seconds: data.love_sec }),
    stomach: duration({ seconds: data.stomach_sec }),
    toothAcidity: duration({ seconds: data.tooth_acidity_sec }),
    urine: duration({ seconds: data.urine_sec }),
  };
  return acc;
}, {} as AttributeDecayValues);

export const decayMultipliers: Record<PetAttribute, number> = {
  love: -1,
  urine: 1,
  stomach: -1,
  dirtiness: 1,
  toothAcidity: -1,
};

export const petStatusEffects = [
  'dead',
  'asleep',
  'dying',
  'evolving',
  'hadVisitor',
  'sick',
  'toothDecay',
  'scared',
  'wantToGoOut',
] as const;

export const statusEffectInterval = duration({ minutes: 5 });

export const shouldGetSick = (stress: number, random: number) =>
  random < stress / 2000;

export const shouldGetToothDecay = (toothAcidity: number, random: number) =>
  random < toothAcidity / 1000;

export const scaredCoefficients = PetSpeciesData.reduce((acc, data) => {
  acc[data.id] = data.scared_coefficient;
  return acc;
}, {} as Record<PetSpecies, number>);

export const shouldGetScared = (
  species: PetSpecies | undefined,
  love: { current: number; max: number },
  random: number,
) =>
  species &&
  random < (scaredCoefficients[species] * (love.max - love.current)) / 1000;

export const shouldWantToGoOut = (timeSinceLastVisit: number, random: number) =>
  random < (0.0001 * timeSinceLastVisit) / duration({ minutes: 1 });

/** Return the the start and end sleeping period in hours */
export function getSleepingPeriod(state: Record<string, any>) {
  if (typeof state !== 'object') throw new Error('Invalid state');

  // Used for tests only, otherwise we would have to change all hardoced times in the tests
  // It could be resolved by mocking this module but that seems to make jest hang indefinitely in some cases
  if (state?.earlySleepTime) {
    return {
      startHour: 23,
      endHour: 7,
    };
  }

  return {
    startHour: 24,
    endHour: 8,
  };
}

/** Return the length of the sleeping period in hours */
export function getSleepPeriodLength(state: Record<string, any>) {
  if (typeof state !== 'object') throw new Error('Invalid state');
  const sleepingPeriod = getSleepingPeriod(state);
  return sleepingPeriod.endHour + 24 - sleepingPeriod.startHour;
}

export const babyAwakePeriod = duration({ hours: 2 });

const isNewBaby = ({ stage, timestamp }: Pet.Evolution, now: number) =>
  stage === 'baby' && now - timestamp < babyAwakePeriod;

export function shouldBeAsleep(
  evolution: Pet.Evolution | undefined,
  now: number,
  state: Record<string, any>,
) {
  if (!evolution || isNewBaby(evolution, now)) {
    return false;
  }

  const sleepingPeriod = getSleepingPeriod(state);
  const timeZone = getLocalTimezone(state);
  const jstHours = getLocalDate(now, timeZone).getHours();
  const { startHour, endHour } = sleepingPeriod;
  return jstHours >= startHour || jstHours < endHour;
}

export function getTimeUntilAwake(
  evolution: Pet.Evolution,
  now: number,
  state: Record<string, any>,
) {
  const timeZone = getLocalTimezone(state);
  if (!shouldBeAsleep(evolution, now, state)) {
    return 0;
  }

  const sleepingPeriod = getSleepingPeriod(state);
  let awakeTime = now;
  let jstNow = getLocalDate(now, timeZone);
  if (jstNow.getHours() >= sleepingPeriod.startHour) {
    awakeTime += duration({ hours: 24 - sleepingPeriod.startHour });
  }
  jstNow = getLocalDate(awakeTime, timeZone);
  if (jstNow.getHours() < sleepingPeriod.endHour) {
    awakeTime += duration({
      hours: sleepingPeriod.endHour - jstNow.getHours(),
      minutes: -jstNow.getMinutes(),
      seconds: -jstNow.getSeconds(),
      ms: -jstNow.getMilliseconds(),
    });
  }
  return awakeTime - now;
}

export function getAwakeDuration(
  evolution: Pet.Evolution | undefined,
  petCreatedAt: number,
  start: number,
  end: number,
  state: Record<string, any>,
) {
  if (!evolution.stage) {
    return 0;
  }

  const timeZone = getLocalTimezone(state);
  const sleepingPeriod = getSleepingPeriod(state);
  let awakeDuration = 0;

  // Ensure we're not accruing time before the pet was created
  start = Math.max(start, petCreatedAt);

  // Accrue any time that a newly created pet is awake during the night
  const babyAwakePeriodEnd = Math.min(petCreatedAt + babyAwakePeriod, end);
  if (evolution.stage === 'baby' && start < babyAwakePeriodEnd) {
    awakeDuration += babyAwakePeriodEnd - start;
    start = babyAwakePeriodEnd;
  }

  // If we're starting while asleep, skip ahead to the morning
  start += getTimeUntilAwake(evolution, start, state);

  // Accrue time for each complete day between start and end
  const completeDays = Math.floor(
    Math.max(0, end - start) / duration({ days: 1 }),
  );
  awakeDuration +=
    completeDays *
    duration({
      hours: sleepingPeriod.startHour - sleepingPeriod.endHour,
    });
  start += duration({ days: completeDays });

  // If we're ending while asleep, skip back to the previous night
  let jstEnd = getLocalDate(end, timeZone);
  if (jstEnd.getHours() < sleepingPeriod.endHour) {
    end -= duration({ hours: jstEnd.getHours() + 1 });
  }
  jstEnd = getLocalDate(end, timeZone);
  if (jstEnd.getHours() >= sleepingPeriod.startHour) {
    end -= duration({
      hours: jstEnd.getHours() - sleepingPeriod.startHour,
      minutes: jstEnd.getMinutes(),
      seconds: jstEnd.getSeconds(),
      ms: jstEnd.getMilliseconds(),
    });
  }

  // Return current awake duration if we've skipped past the end time
  if (start >= end) {
    return awakeDuration;
  }

  // Accrue time for last partial day
  awakeDuration += end - start;

  // Subtract any overnight period
  if (
    getLocalDate(start, timeZone).getDay() !==
    getLocalDate(end, timeZone).getDay()
  ) {
    awakeDuration -= duration({
      hours: 24 - sleepingPeriod.startHour + sleepingPeriod.endHour,
    });
  }

  return awakeDuration;
}

export const evolutions = PetSpeciesData.reduce((acc, petEvolution) => {
  acc[petEvolution.id] = petEvolution;
  return acc;
}, {} as Record<PetEvolution['id'], PetEvolution>);

export const species = Array.from(new Set(PetSpeciesData.map(({ id }) => id)));
export const petSpeciesTuple = tuple(...species);
export type SpeciesID = typeof petSpeciesTuple[number];

export const evolutionStages = PetSpeciesData.reduce((acc, petEvolution) => {
  const { stage } = petEvolution;
  if (!acc[stage]) {
    acc[stage] = [];
  }
  acc[stage].push(petEvolution);
  return acc;
}, {} as Record<PetEvolutionStage, PetEvolution[]>);

export const evolutionSequence = tuple<PetEvolutionStage>(
  'baby',
  'kid',
  'youth',
  'adult',
);

export const timeToEvolve = {
  baby: duration({ hours: 1 }),
  kid: duration({ hours: 14 }),
  youth: duration({ hours: 24 }),
};
